'use client';
import React from 'react';
import { Clock, ExternalLink } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import EmptyState from '@/components/ui/EmptyState';
import { CalendarCheck } from 'lucide-react';
import { Appointment } from './schedulingTypes';

interface Props {
  appointments: Appointment[];
  weekDates: Date[];
  formatDateKey: (d: Date) => string;
  days: string[];
}

const statusBadge: Record<string, 'active' | 'pending' | 'info' | 'inactive' | 'warning'> = {
  scheduled: 'info',
  confirmed: 'active',
  'in-progress': 'warning',
  completed: 'active',
  cancelled: 'inactive',
  rescheduled: 'warning',
};

export default function AppointmentListView({ appointments, weekDates, formatDateKey, days }: Props) {
  if (appointments.length === 0) {
    return (
      <div className="bg-card rounded-xl border border-border">
        <EmptyState
          icon={<CalendarCheck size={28} />}
          title="No appointments this week"
          description="No appointments are scheduled for this week. Create a new appointment to assign jobs to technicians."
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {weekDates.map((date, i) => {
        const dateKey = formatDateKey(date);
        const dayAppts = appointments
          .filter((a) => a.scheduledDate === dateKey)
          .sort((a, b) => a.scheduledTime.localeCompare(b.scheduledTime));

        if (dayAppts.length === 0) return null;

        return (
          <div key={`listday-${dateKey}`} className="bg-card rounded-xl border border-border overflow-hidden">
            <div className="px-5 py-3 border-b border-border bg-muted/30 flex items-center justify-between">
              <div>
                <span className="font-700 text-foreground">{days[i]}, {dateKey}</span>
                <span className="ml-2 text-xs text-muted-foreground">{dayAppts.length} appointment{dayAppts.length !== 1 ? 's' : ''}</span>
              </div>
            </div>
            <div className="divide-y divide-border">
              {dayAppts.map((appt) => (
                <div key={appt.id} className="px-5 py-4 hover:bg-muted/30 transition-colors group flex items-start gap-4">
                  <div className="text-center w-14 shrink-0">
                    <div className="flex items-center gap-1 text-primary">
                      <Clock size={12} />
                      <span className="text-xs font-700 tabular-nums">{appt.scheduledTime}</span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-700 text-foreground">{appt.customerName}</span>
                      <span className="font-mono text-xs text-primary bg-secondary px-1.5 py-0.5 rounded">{appt.customerNumber}</span>
                      <Badge variant={statusBadge[appt.status]}>{appt.status}</Badge>
                      {appt.rebookingFee > 0 && (
                        <span className="text-xs bg-red-50 text-danger border border-red-200 px-1.5 py-0.5 rounded font-600">
                          +₱{appt.rebookingFee} rebooking
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5 truncate">{appt.address}</p>
                    <div className="flex items-center gap-4 mt-1.5 text-xs text-muted-foreground flex-wrap">
                      <span>Tech: <span className="font-600 text-foreground">{appt.assignedTechnicianName || 'Unassigned'}</span></span>
                      <span>Payment: <span className="font-600 text-foreground">{appt.paymentMode}</span></span>
                      <span>Machine: <span className="font-600 text-foreground">{appt.machineModel}</span></span>
                    </div>
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {(['sedi', 'pre', 'mem', 'post'] as const).map((f) => (
                        <span
                          key={`list-${appt.id}-${f}`}
                          className={`text-xs px-2 py-0.5 rounded font-600 ${
                            appt.filters[f] ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {f.charAt(0).toUpperCase() + f.slice(1)}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 text-xs text-primary font-600 hover:underline shrink-0 mt-1">
                    <ExternalLink size={12} />
                    Profile
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}